/**
 * Subscription Drawer — Config-Driven
 *
 * Shows current subscription status, billing period toggle,
 * upgrade comparison card, and cancel/retention flow.
 *
 * ALL tier-specific data is read from TIER_CONFIG (subscriptionTiers.ts).
 * No hardcoded tier names, feature lists, or pricing.
 *
 * Sprint 2, Day 6 (Feb 5, 2026)  — Original
 * Refactored: Feb 13, 2026        — Config-driven + UpgradeComparisonCard + RetentionScreen
 *
 * Mission 33 — Subscription System
 */

import React, { useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { PaywallModal } from '@/components/PaywallModal';
import { UpgradeComparisonCard } from '@/components/subscription/UpgradeComparisonCard';
import { RetentionScreen } from '@/components/subscription/RetentionScreen';
import {
  getTierConfig,
  getUpgradeTier,
  getAnnualSavingsText,
  getPlanFeatureBullets,
} from '@/config/subscriptionTiers';
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';
import type { SubscriptionPeriod, SubscriptionTier } from '@/services/subscriptionService';
import { CoachCedarColors } from '@/utils/styleConverter';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SubscriptionDrawerProps {
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubscriptionDrawer({ onClose }: SubscriptionDrawerProps) {
  const { user } = useAuth();
  const {
    tier,
    status,
    isPremium,
    isTrial,
    isBetaTester,
    expiresAt,
    periodType,
    isLoading,
    refreshSubscription,
  } = useSubscription();

  // UI state
  const [showPaywall, setShowPaywall] = useState(false);
  const [showRetention, setShowRetention] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<SubscriptionPeriod>(periodType);

  // -------------------------------------------------------------------------
  // Derived config
  // -------------------------------------------------------------------------

  const tierConfig = (() => {
    try {
      return getTierConfig(tier);
    } catch {
      return getTierConfig('trial'); // Safe fallback
    }
  })();

  const upgradeTier = (() => {
    try {
      return getUpgradeTier(tier);
    } catch {
      return null;
    }
  })();

  const tierDisplayName = isBetaTester
    ? `${getTierConfig('extended').displayName} (Beta Tester)`
    : tierConfig.displayName;

  const tierColor = isPremium || isBetaTester ? CoachCedarColors.cedarPrime : '#999';

  const statusLabel = (() => {
    if (isBetaTester) return 'Active (Lifetime)';
    if (status === 'active') return 'Active';
    if (status === 'trial') return 'Trial Active';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'expired') return 'Expired';
    return 'Unknown';
  })();

  const formattedExpiration = expiresAt
    ? new Date(expiresAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  // Price to display in the current plan card
  const displayPrice =
    billingPeriod === 'monthly'
      ? tierConfig.pricing.monthly
      : tierConfig.pricing.annualMonthly;

  const annualSavings = getAnnualSavingsText(tier);

  // Localized prices from RevenueCat (falls back to static USD on web)
  const prices = useSubscriptionPrices();

  // Platform detection (web component — use navigator.userAgent)
  const isAndroid =
    typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);
  const healthPlatformLabel = isAndroid ? 'Google Fit' : 'Apple Health';

  // Feature bullets reused from the trial paywall
  const featureBullets = getPlanFeatureBullets(healthPlatformLabel);

  // Dynamic price strings for the current plan card
  const currentPlanPriceDisplay = (() => {
    if (tier === 'full') {
      return billingPeriod === 'annual' ? prices.fullAnnual : prices.fullMonthly;
    }
    if (tier === 'extended') {
      return billingPeriod === 'annual' ? prices.extendedAnnual : prices.extendedMonthly;
    }
    return null;
  })() ?? '—';
  const currentPlanPeriodSuffix = billingPeriod === 'annual' ? '/yr' : '/mo';

  // Localized annual billing amount (for "Billed as X/year" note)
  const currentPlanAnnualPriceDisplay = (() => {
    if (tier === 'full') return prices.fullAnnual;
    if (tier === 'extended') return prices.extendedAnnual;
    return null;
  })() ?? '—';

  // Dynamic price strings to pass to the upgrade comparison card
  const upgradeToId = upgradeTier?.id;
  const upgradeCardPriceMonthly =
    upgradeToId === 'full'
      ? prices.fullMonthly
      : upgradeToId === 'extended'
      ? prices.extendedMonthly
      : undefined;
  const upgradeCardPriceAnnual =
    upgradeToId === 'full'
      ? prices.fullAnnual
      : upgradeToId === 'extended'
      ? prices.extendedAnnual
      : undefined;

  // Current tier localized prices (for the "Current" column in UpgradeComparisonCard)
  const currentCardPriceMonthly =
    tier === 'full' ? prices.fullMonthly : (tier === 'extended' ? prices.extendedMonthly : undefined);
  const currentCardPriceAnnual =
    tier === 'full' ? prices.fullAnnual : (tier === 'extended' ? prices.extendedAnnual : undefined);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  // Only open paywall when user ID is set so RevenueCat can load plans (avoids endless "Loading plans...").
  const handleUpgradeFromCard = (upgradeTierId: SubscriptionTier) => {
    if (user?.id) setShowPaywall(true);
  };

  const handleTrialUpgrade = () => {
    if (user?.id) setShowPaywall(true);
  };

  const handlePurchaseSuccess = async () => {
    await refreshSubscription();
    setShowPaywall(false);
  };

  const handleCancelTap = () => {
    setShowRetention(true);
  };

  const handleStay = () => {
    setShowRetention(false);
  };

  const handleCancelComplete = async () => {
    setShowRetention(false);
    await refreshSubscription();
  };

  const handleManageSubscription = () => {
    if (typeof window !== 'undefined') {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /android/i.test(navigator.userAgent);

      if (isIOS) {
        window.open('https://apps.apple.com/account/subscriptions', '_blank');
      } else if (isAndroid) {
        window.open('https://play.google.com/store/account/subscriptions', '_blank');
      } else {
        // Web users — show helpful message
        alert('Manage your subscription through the iOS App Store or Google Play Store.');
      }
    }
  };

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="subscription-drawer" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: '#999' }}>Loading subscription...</div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Retention screen (replaces whole drawer when active)
  // -------------------------------------------------------------------------

  if (showRetention) {
    return (
      <div style={{ height: '100%' }}>
        <RetentionScreen
          currentTier={tier}
          onStay={handleStay}
          onCancelComplete={handleCancelComplete}
          renewalDate={expiresAt ? expiresAt.toISOString() : null}
        />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Main drawer
  // -------------------------------------------------------------------------

  return (
    <>
      <div className="subscription-drawer" style={{ padding: '20px' }}>
        {/* Header */}
        <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#113C2B', marginBottom: '8px' }}>
          Subscription
        </h2>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '32px' }}>
          Manage your Coach Cedar subscription and billing
        </p>

        {/* ── Current Plan Card ───────────────────────────────── */}
        <div
          style={{
            background: '#F8F9FA',
            border: `2px solid ${tierColor}`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
          }}
        >
          {/* Tier name + status badge */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: tierColor, margin: 0 }}>
              {tierDisplayName}
            </h3>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#fff',
                background: tierColor,
                padding: '4px 12px',
                borderRadius: '12px',
              }}
            >
              {statusLabel}
            </div>
          </div>

          {/* Pricing row (only for paid tiers) */}
          {tierConfig.pricing.monthly > 0 && !isBetaTester && (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '6px',
                  marginBottom: '8px',
                }}
              >
                <span style={{ fontSize: '28px', fontWeight: '700', color: '#113C2B' }}>
                  {currentPlanPriceDisplay}
                </span>
                <span style={{ fontSize: '14px', color: '#666' }}>{currentPlanPeriodSuffix}</span>
                {billingPeriod === 'annual' && annualSavings && (
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#fff',
                      background: '#42A5A5',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      marginLeft: '4px',
                    }}
                  >
                    {annualSavings}
                  </span>
                )}
              </div>

              {/* Monthly / Annual toggle */}
              <div
                style={{
                  display: 'inline-flex',
                  background: '#ECECEC',
                  borderRadius: '8px',
                  padding: '3px',
                }}
              >
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  style={{
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: billingPeriod === 'monthly' ? '600' : '400',
                    color: billingPeriod === 'monthly' ? '#113C2B' : '#888',
                    background: billingPeriod === 'monthly' ? '#fff' : 'transparent',
                    cursor: 'pointer',
                    boxShadow: billingPeriod === 'monthly' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  style={{
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: billingPeriod === 'annual' ? '600' : '400',
                    color: billingPeriod === 'annual' ? '#113C2B' : '#888',
                    background: billingPeriod === 'annual' ? '#fff' : 'transparent',
                    cursor: 'pointer',
                    boxShadow: billingPeriod === 'annual' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Annual{annualSavings ? ` (${annualSavings})` : ''}
                </button>
              </div>

              {billingPeriod === 'annual' && (
                <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                    Billed as {currentPlanAnnualPriceDisplay}/year
                </div>
              )}
            </div>
          )}

          {/* Features — universal + tier differentiators */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              <strong>Your Plan Includes:</strong>
            </div>
            <ul
              style={{
                fontSize: '14px',
                color: '#444',
                lineHeight: '1.8',
                paddingLeft: '20px',
                margin: 0,
              }}
            >
              {/* Feature bullets — same as the trial paywall */}
              {featureBullets.map((b, i) => (
                <li key={`feat-${i}`}>
                  {b.icon} <strong>{b.heading}</strong> {'\u2014'} {b.description}
                </li>
              ))}
            </ul>
          </div>

          {/* Expiration / renewal date */}
          {formattedExpiration && !isBetaTester && (
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #E0E0E0',
                fontSize: '14px',
                color: '#666',
              }}
            >
              {status === 'cancelled' ? (
                <>
                  Access until: <strong>{formattedExpiration}</strong>
                </>
              ) : status === 'expired' ? (
                <>
                  Expired: <strong>{formattedExpiration}</strong>
                </>
              ) : isTrial || tier === 'trial' ? (
                <>
                  Trial ends: <strong>{formattedExpiration}</strong>
                </>
              ) : (
                <>
                  Renews: <strong>{formattedExpiration}</strong>
                </>
              )}
            </div>
          )}

          {/* Beta tester badge */}
          {isBetaTester && (
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #E0E0E0',
                fontSize: '14px',
                color: tierColor,
                fontWeight: '500',
              }}
            >
              Thanks for being a beta tester! Lifetime CC Pro access.
            </div>
          )}
        </div>

        {/* ── Upgrade Comparison Card ─────────────────────────── */}
        {!isBetaTester && upgradeTier && (
          <div style={{ marginBottom: '24px' }}>
            <UpgradeComparisonCard
              currentTierId={tier}
              onUpgrade={handleUpgradeFromCard}
              defaultPeriod={billingPeriod}
              priceStringMonthly={upgradeCardPriceMonthly}
              priceStringAnnual={upgradeCardPriceAnnual}
              currentPriceStringMonthly={currentCardPriceMonthly ?? undefined}
              currentPriceStringAnnual={currentCardPriceAnnual ?? undefined}
            />
          </div>
        )}

        {/* ── Trial Upgrade CTA (active trial only) ──────────── */}
        {isTrial && status !== 'expired' && !isBetaTester && (
          <>
            <button
              onClick={handleTrialUpgrade}
              style={{
                background: CoachCedarColors.cedarPrime,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%',
                marginBottom: '8px',
              }}
            >
              Upgrade to Premium
            </button>
            {/* Auto-renewal disclosure — required by Apple & Google */}
            <p
              style={{
                fontSize: '11px',
                color: '#888',
                textAlign: 'center' as const,
                lineHeight: '1.5',
                margin: '0 0 16px',
                padding: '0 4px',
              }}
            >
              {isAndroid ? (
                <>
                  Payment is charged to your Google Play account. Your subscription
                  auto-renews at {prices.fullMonthly ?? 'the plan price'}/month after
                  the free trial unless canceled before the trial ends. Manage or
                  cancel in <strong>Google Play &gt; Subscriptions</strong>. By
                  subscribing, you agree to our{' '}
                  <a
                    href="https://coachcedar.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://coachcedar.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Privacy Policy
                  </a>
                  .
                </>
              ) : (
                <>
                  Payment is charged to your Apple ID. Your subscription auto-renews
                  at {prices.fullMonthly ?? 'the plan price'}/month after the free
                  trial unless canceled at least 24 hours before the end of the trial
                  period. Manage or cancel in{' '}
                  <strong>Settings &gt; Subscriptions</strong>. By subscribing, you
                  agree to our{' '}
                  <a
                    href="https://coachcedar.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://coachcedar.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Privacy Policy
                  </a>
                  .
                </>
              )}
            </p>
          </>
        )}

        {/* ── Expired Trial CTA ────────────────────────────────── */}
        {status === 'expired' && !isBetaTester && (
          <>
            <div
              style={{
                background: '#FFF8E1',
                border: '1px solid #FFE082',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#795548',
                lineHeight: '1.5',
              }}
            >
              Your free trial has ended. Subscribe to continue your coaching
              journey with Cedar.
            </div>
            <button
              onClick={handleTrialUpgrade}
              style={{
                background: CoachCedarColors.cedarPrime,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%',
                marginBottom: '8px',
              }}
            >
              Subscribe Now
            </button>
            {/* Auto-renewal disclosure — required by Apple & Google */}
            <p
              style={{
                fontSize: '11px',
                color: '#888',
                textAlign: 'center' as const,
                lineHeight: '1.5',
                margin: '0 0 16px',
                padding: '0 4px',
              }}
            >
              {isAndroid ? (
                <>
                  Payment is charged to your Google Play account at confirmation.
                  Your subscription auto-renews at{' '}
                  {prices.fullMonthly ?? 'the plan price'}/month unless canceled
                  before the renewal date. Manage or cancel in{' '}
                  <strong>Google Play &gt; Subscriptions</strong>. By
                  subscribing, you agree to our{' '}
                  <a
                    href="https://coachcedar.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://coachcedar.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Privacy Policy
                  </a>
                  .
                </>
              ) : (
                <>
                  Payment is charged to your Apple ID at confirmation. Your
                  subscription auto-renews at{' '}
                  {prices.fullMonthly ?? 'the plan price'}/month unless canceled
                  at least 24 hours before the renewal date. Manage or cancel in{' '}
                  <strong>Settings &gt; Subscriptions</strong>. By subscribing,
                  you agree to our{' '}
                  <a
                    href="https://coachcedar.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://coachcedar.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: CoachCedarColors.cedarPrime }}
                  >
                    Privacy Policy
                  </a>
                  .
                </>
              )}
            </p>
          </>
        )}

        {/* ── Manage / Cancel Buttons ─────────────────────────── */}
        {isPremium && status === 'active' && !isBetaTester && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
            <button
              onClick={handleManageSubscription}
              style={{
                background: 'white',
                color: CoachCedarColors.cedarPrime,
                border: `2px solid ${CoachCedarColors.cedarPrime}`,
                borderRadius: '8px',
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Manage Subscription
            </button>
            <button
              onClick={handleCancelTap}
              style={{
                background: 'transparent',
                color: '#999',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Cancel Subscription
            </button>
          </div>
        )}

        {/* ── Help Text ───────────────────────────────────────── */}
        <div
          style={{
            marginTop: '32px',
            padding: '16px',
            background: '#F8F9FA',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#666',
            lineHeight: '1.6',
          }}
        >
          <strong style={{ color: CoachCedarColors.cedarPrime }}>Need help?</strong>
          <br />
          Questions about billing? Email us at{' '}
          <a href="mailto:support@coachcedar.com" style={{ color: CoachCedarColors.cedarPrime }}>
            support@coachcedar.com
          </a>
        </div>
      </div>

      {/* ── Paywall Modal ──────────────────────────────────── */}
      {showPaywall && (
        <PaywallModal
          visible={showPaywall}
          onClose={() => setShowPaywall(false)}
          onPurchaseSuccess={handlePurchaseSuccess}
          mode="upgrade"
          showCloseButton
          userId={user?.id ?? null}
          currentTier={tier}
          currentPeriodType={periodType}
          expiresAt={expiresAt}
          subscriptionStatus={status}
          isBetaTester={isBetaTester}
        />
      )}
    </>
  );
}
